import { React, useState, useEffect } from "react";
import styled from "styled-components";
import tramData from "../../assets/data/manchester.json";
import { createLineColourResolver } from "../../assets/libs/lineColours";

const resolveLineColour = createLineColourResolver(tramData);

const DeparturesSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`

const NoDepartures = styled.div`
    font-size: 13px;
    color: #8a8a8a;
    padding: 4px 0;
`

const Spinner = styled.div`
    align-self: center;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-left-color: #c41306;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`

const Card = styled.div`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #e6e6e6;
    border-left: 4px solid ${({ color }) => color || "#c8c8c8"};
    width: 100%;
`

const Dot = styled.span`
    flex-shrink: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ color }) => color || "#c8c8c8"};
`

const Info = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const Dest = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #0d0c1d;
`

const Sub = styled.span`
    font-size: 12px;
    color: #8a8a8a;
    text-transform: capitalize;
`

const Time = styled.span`
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0d0c1d;
    white-space: nowrap;

    &.due {
        color: #1e7d3c;
    }
`

const DepartureCard = ({ departure }) => {
    const color = resolveLineColour(departure.destination);
    const due = departure.wait === 0;
    const time = due ? "Due" : `${departure.wait} min`;

    const display = departure.destinationDisplay || departure.destination;
    const [primary, via] = String(display).split(/\s+via\s+/i);
    const sub = [via ? `via ${via}` : null, departure.carriages]
        .filter(Boolean)
        .join(" · ") || "Tram";

    return (
        <Card color={color}>
            <Dot color={color} />
            <Info>
                <Dest>To {primary}</Dest>
                <Sub>{sub}</Sub>
            </Info>
            <Time className={due ? "due" : ""}>{time}</Time>
        </Card>
    );
};

const Departures = ({ tramstop, open }) => {
    const [stopName, setStopName] = useState(tramstop);
    const [departures, setDepartures] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setStopName(tramstop);
    }, [tramstop]);

    useEffect(() => {
        const name = stopName?.name;
        if (!name || !open) {
            return;
        }

        const load = () => {
            fetch("/trams/" + name)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data) => {
                    const upcoming = data
                        .filter((d) => d.destinationDisplay !== "Not in Service" && d.destinationDisplay !== "See Tram Front")
                        .sort((a, b) => (a.wait ?? Infinity) - (b.wait ?? Infinity));
                    setDepartures(upcoming);
                })
                .catch((error) => {
                    console.error("Error fetching tram departures: ", error);
                })
                .finally(() => {
                    setLoading(false);
                });
        };

        setDepartures([]);
        setLoading(true);
        load();

        // Refresh every 30s, but only while the dialog is open.
        const interval = setInterval(load, 30000);

        return () => clearInterval(interval);
    }, [stopName, open]);

    return (
        <DeparturesSection>
            {loading ? (
                <Spinner />
            ) : departures.length > 0 ? (
                departures.map((departure, index) => (
                    <DepartureCard key={index} departure={departure} />
                ))
            ) : (
                <NoDepartures>No upcoming trams.</NoDepartures>
            )}
        </DeparturesSection>
    );
};

export default Departures;
